import { create } from 'zustand';
import api from '../lib/api';

// ── Types ──────────────────────────────────────────────────────────────

interface CostSummary {
  today: number;
  periodTotal: number;
  dailyAverage: number;
  byDay: Array<{ date: string; cost: number }>;
}

interface RunSummary {
  startedAt: string;
  finishedAt: string | null;
  status: string;
  type: 'twitter' | 'facebook' | 'enrichment' | 'screenshot' | 'other';
  platform: string | null;
  itemCount: number;
  cost: number;
}

interface HealthStats {
  totalActive: number;
  withGoal: number;
  withYear: number;
  withState: number;
  matchedToOrg: number;
  byPlatform: Record<string, number>;
  bySource: Record<string, number>;
}

interface DiscoveryStats {
  totalDiscovered: number;
  periodNew: number;
  last24Hours: number;
  byDay: Array<{ date: string; count: number }>;
}

export interface ScrapingDashboardData {
  costs: CostSummary;
  recentRuns: RunSummary[];
  health: HealthStats;
  discovery: DiscoveryStats;
}

// ── Store ──────────────────────────────────────────────────────────────

interface AdminState {
  scrapingData: ScrapingDashboardData | null;
  isLoading: boolean;
  error: string | null;
  period: 7 | 14 | 30 | 90;
  fetchScrapingData: () => Promise<void>;
  setPeriod: (period: 7 | 14 | 30 | 90) => void;
}

export const useAdminStore = create<AdminState>((set, get) => ({
  scrapingData: null,
  isLoading: false,
  error: null,
  period: 30,

  fetchScrapingData: async () => {
    set({ isLoading: true, error: null });
    try {
      const { period } = get();
      const { data } = await api.get(`/api/admin/competitor-scraping?period=${period}`);
      set({ scrapingData: data, isLoading: false });
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Failed to load scraping data',
        isLoading: false,
      });
    }
  },

  setPeriod: (period) => {
    set({ period });
    get().fetchScrapingData();
  },
}));
